import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import { Button, StatusPill } from "@/components/kit";

const KIND_LABEL = { meeting: "Meeting attendance", event: "Event attendance", payment: "Membership payment", donation: "Donation", volunteer: "Volunteering", household: "Household attendance", redemption: "Reward redeemed", redemption_refund: "Redemption refunded" };
const fmt = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const REWARD_TONE = { pending: "gold", fulfilled: "olive", cancelled: "gray" };

export default function MemberRewards() {
  const [balance, setBalance] = useState(0);
  const [ledger, setLedger] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [mine, setMine] = useState([]);

  const load = useCallback(async () => {
    try {
      const [p, r, m] = await Promise.all([api.get("/points/me"), api.get("/rewards"), api.get("/rewards/me/redemptions")]);
      setBalance(p.data.balance); setLedger(p.data.ledger || []);
      setRewards(r.data.items || []); setMine(m.data.items || []);
    } catch (e) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const redeem = async (rw) => {
    if (balance < rw.point_cost) return toast.error("Not enough points yet.");
    try { const res = await api.post(`/rewards/${rw.reward_id}/redeem`); toast.success("Redeemed! Pick it up soon."); setBalance(res.data.new_balance); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not redeem"); }
  };

  return (
    <div className="space-y-5" data-testid="member-rewards-page">
      <div className="rounded-2xl p-6 bg-ink text-cream relative overflow-hidden" data-testid="rewards-balance-card">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 85% 15%, #daa405 0, transparent 45%)" }} />
        <div className="relative z-10">
          <div className="text-[12px] uppercase tracking-widest text-gold font-bold">Your points</div>
          <div className="font-zilla text-5xl font-semibold mt-1" data-testid="rewards-balance">{balance}</div>
          <div className="text-cream/70 text-[14px] mt-1">Earn points by attending, paying dues, and volunteering.</div>
        </div>
      </div>

      <div>
        <h2 className="font-zilla text-2xl font-semibold mb-3">Reward catalog</h2>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="rewards-catalog">
          {rewards.length === 0 && <div className="bg-cream border border-ink/15 rounded-xl p-8 text-center text-ink/55 sm:col-span-2">No rewards available yet. Check back soon!</div>}
          {rewards.map((rw) => {
            const afford = balance >= rw.point_cost && rw.in_stock;
            return (
              <div key={rw.reward_id} data-testid={`reward-${rw.reward_id}`} className="bg-cream border border-ink/15 rounded-xl p-4 flex flex-col">
                <div className="flex items-start gap-2"><Icon name="Gift" className="w-5 h-5 text-gold shrink-0 mt-0.5" /><div className="min-w-0"><div className="text-[16px] font-semibold">{rw.name}</div>{rw.description && <div className="text-[13px] text-ink/60">{rw.description}</div>}</div></div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-ink/8">
                  <span className="font-zilla text-xl font-semibold">{rw.point_cost} <span className="text-[13px] text-ink/50 font-sans">pts</span></span>
                  <Button data-testid={`redeem-${rw.reward_id}`} disabled={!afford} onClick={() => redeem(rw)} className="min-h-[40px] py-2 text-[14px]">{!rw.in_stock ? "Out of stock" : afford ? "Redeem" : "Not enough"}</Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {mine.length > 0 && (
        <div data-testid="my-redemptions">
          <h2 className="font-zilla text-2xl font-semibold mb-3">Your redemptions</h2>
          <div className="bg-cream border border-ink/15 rounded-xl overflow-hidden">
            {mine.map((r) => (
              <div key={r.redemption_id} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-ink/8 last:border-0">
                <div><div className="font-semibold text-[15px]">{r.reward_name}</div><div className="text-[12px] text-ink/50">{r.point_cost} pts · {fmt(r.created_at)}</div></div>
                <StatusPill tone={REWARD_TONE[r.status] || "gray"} className="text-[12px] px-2.5 py-1 capitalize">{r.status}</StatusPill>
              </div>
            ))}
          </div>
        </div>
      )}

      <div data-testid="points-ledger">
        <h2 className="font-zilla text-2xl font-semibold mb-3">Points history</h2>
        <div className="bg-cream border border-ink/15 rounded-xl overflow-hidden">
          {ledger.length === 0 && <div className="p-8 text-center text-ink/55">No points yet.</div>}
          {ledger.map((e) => (
            <div key={e.pe_id} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-ink/8 last:border-0">
              <div><div className="text-[15px]">{KIND_LABEL[e.kind] || e.kind}</div><div className="text-[12px] text-ink/50">{fmt(e.at)}</div></div>
              <span className={`font-semibold text-[15px] ${e.points < 0 ? "text-maroon" : "text-dark-olive"}`}>{e.points > 0 ? "+" : ""}{e.points}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
